import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Stack,
  Card,
  Popover,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { deleteNote, getNotes } from "../../services/noteService";
import {
  deleteAttachment,
  downloadAttachment,
} from "../../services/attachmentService";
import NoteForm from "../Forms/NoteForm";
import AttachmentForm from "../Forms/AttachmentForm";
import ConfirmDialog from "../Common/ConfirmDialog";
import Notification from "../Common/Notification";
import { useTranslation } from "react-i18next";
import useAcl from "../../hooks/useAcl";
import useEntityPermissions from "../../hooks/useEntityPermissions";
import { Note } from "../../types";

interface Props {
  parentId: string;
  onRefresh: () => Promise<void>;
}

const NotesAccordion: React.FC<Props> = ({ parentId, onRefresh }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [attachmentFormOpen, setAttachmentFormOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [attachmentConfirmOpen, setAttachmentConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  const [attachmentToDelete, setAttachmentToDelete] = useState<string | null>(
    null,
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [attachmentAnchorEl, setAttachmentAnchorEl] =
    useState<null | HTMLElement>(null);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [noteForEdit, setNoteForEdit] = useState<Note | null>(null);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<
    string | null
  >(null);
  const [selectedNoteForAttachments, setSelectedNoteForAttachments] =
    useState<Note | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);

  const { canCreate } = useAcl("Note");
  const notePermissions = useEntityPermissions("Note", notes);
  const attachmentPermissions = useEntityPermissions(
    "Attachment",
    selectedNoteForAttachments?.attachmentsIds?.map((id) => ({
      id,
      name:
        selectedNoteForAttachments?.attachmentsNames?.[id] || "Unknown file",
      url: `/api/attachment/${id}`,
      type: selectedNoteForAttachments?.attachmentsTypes?.[id],
    })) || [],
  );

  const validNotes = notes.filter((n) => n.post && n.post.trim() !== "");

  const loadNotes = useCallback(async () => {
    if (!parentId || parentId === "new") {
      setNotes([]);
      return;
    }
    try {
      const loadedNotes = await getNotes(parentId);
      setNotes(loadedNotes);
    } catch (err) {
      console.error("Ошибка при загрузке заметок:", err);
      setNotes([]);
    }
  }, [parentId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleDelete = (id: string) => {
    setNoteToDelete(id);
    setConfirmOpen(true);
    setAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    if (noteToDelete) {
      setLoading(true);
      setError(null);
      setSuccess(null);
      try {
        await deleteNote(noteToDelete);
        await onRefresh();
        await loadNotes();
        setSuccess(t("notes.deleteSuccess"));
        setTimeout(() => {
          setConfirmOpen(false);
          setNoteToDelete(null);
        }, 1000);
      } catch {
        setError(t("notes.deleteError"));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setNoteToDelete(null);
  };

  const handleAttachmentDelete = (attachmentId: string) => {
    setAttachmentToDelete(attachmentId);
    setAttachmentConfirmOpen(true);
    setAttachmentAnchorEl(null);
  };

  const handleConfirmAttachmentDelete = async () => {
    if (attachmentToDelete && selectedNoteForAttachments) {
      setLoading(true);
      setError(null);
      setSuccess(null);
      try {
        await deleteAttachment(parentId, attachmentToDelete);
        await onRefresh();
        await loadNotes();
        setSuccess(t("attachments.deleteSuccess"));
        setTimeout(() => {
          setAttachmentConfirmOpen(false);
          setAttachmentToDelete(null);
        }, 1000);
      } catch {
        setError(t("attachments.deleteError"));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancelAttachmentDelete = () => {
    setAttachmentConfirmOpen(false);
    setAttachmentToDelete(null);
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    noteId: string,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedNoteId(noteId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedNoteId(null);
  };

  const handleOpenAttachmentMenu = (
    event: React.MouseEvent<HTMLElement>,
    note: Note,
  ) => {
    setAttachmentAnchorEl(event.currentTarget);
    setSelectedNoteForAttachments(note);
  };

  const handleCloseAttachmentMenu = () => {
    setAttachmentAnchorEl(null);
    setSelectedNoteForAttachments(null);
    setSelectedAttachmentId(null);
  };

  const handleEdit = (note: Note) => {
    setNoteForEdit(note);
    setFormOpen(true);
    setAnchorEl(null);
  };

  const handleAttachmentCreated = async () => {
    await onRefresh();
    await loadNotes();
    setAttachmentFormOpen(false);
    setSuccess(t("attachmentForm.uploadSuccess"));
  };

  const handleCloseNotification = () => {
    setError(null);
    setSuccess(null);
  };

  const getAttachmentUrl = (attachmentId: string) => {
    return `/api/attachment/${attachmentId}`;
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        background: "#fff",
        mb: 2,
        minWidth: 280,
        maxWidth: 500,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          justifyContent: "space-between",
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="h4"
            sx={{
              flex: 1,
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
            }}
          >
            {t("notes.title")}
          </Typography>
          <IconButton
            size="small"
            sx={{
              transition: "transform 0.2s",
              mr: 1,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              color: "grey.700",
            }}
            disableRipple
            tabIndex={-1}
            disabled={loading}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
        {canCreate && (
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              setNoteForEdit(null);
              setFormOpen(true);
            }}
            sx={{
              border: "1px solid #4467A5",
              borderRadius: "8px",
              bgcolor: "#4467A5",
              color: "#fff",
              "&:hover": { bgcolor: "#385894" },
            }}
            disabled={loading}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>

      {/* Collapse content */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 1.5 }}>
          {validNotes.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ fontSize: "0.95rem", py: 2, textAlign: "center" }}
            >
              {t("notes.empty")}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {validNotes.map((n) => {
                const { canEdit, canDelete } = notePermissions[n.id] || {
                  canEdit: false,
                  canDelete: false,
                };
                return (
                  <Card
                    key={n.id}
                    sx={{
                      width: "100%",
                      mb: 1,
                      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                      border: "none",
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: { xs: "0.78rem", sm: "0.82rem" },
                        }}
                      >
                        {new Date(n.createdAt).toLocaleDateString(
                          t("notes.dateLocale"),
                        )}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {n.attachmentsIds && n.attachmentsIds.length > 0 && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenAttachmentMenu(e, n)}
                            disabled={loading}
                          >
                            <AttachFileIcon fontSize="small" />
                          </IconButton>
                        )}
                        {(canEdit || canDelete) && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, n.id)}
                            disabled={loading}
                          >
                            <MoreHorizOutlinedIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ width: "100%" }}>
                      <Box
                        sx={{
                          fontSize: { xs: "0.92rem", sm: "1rem" },
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          textAlign: "justify",
                          width: "100%",
                          "& p": {
                            margin: "0 !important",
                          },
                          "& h1, & h2, & h3, & h4, & h5, & h6": {
                            margin: "0 !important",
                            fontWeight: 600,
                          },
                          "& h1": {
                            fontSize: "1.5em",
                          },
                          "& h2": {
                            fontSize: "1.3em",
                          },
                          "& h3": {
                            fontSize: "1.1em",
                          },
                          "& strong": {
                            fontWeight: 600,
                          },
                          "& ul, & ol": {
                            margin: "0 !important",
                            paddingLeft: "1.5em",
                          },
                          "& li": {
                            margin: "0 !important",
                          },
                        }}
                      >
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => (
                              <Typography
                                component="p"
                                sx={{
                                  margin: 0,
                                  fontSize: "inherit",
                                }}
                              >
                                {children}
                              </Typography>
                            ),
                            h1: ({ children }) => (
                              <Typography
                                component="h1"
                                sx={{
                                  margin: 0,
                                  fontSize: "1.5em",
                                  fontWeight: 600,
                                }}
                              >
                                {children}
                              </Typography>
                            ),
                            h2: ({ children }) => (
                              <Typography
                                component="h2"
                                sx={{
                                  margin: 0,
                                  fontSize: "1.3em",
                                  fontWeight: 600,
                                }}
                              >
                                {children}
                              </Typography>
                            ),
                            h3: ({ children }) => (
                              <Typography
                                component="h3"
                                sx={{
                                  margin: 0,
                                  fontSize: "1.1em",
                                  fontWeight: 600,
                                }}
                              >
                                {children}
                              </Typography>
                            ),
                            ul: ({ children }) => (
                              <Box
                                component="ul"
                                sx={{
                                  margin: 0,
                                  paddingLeft: "1.5em",
                                }}
                              >
                                {children}
                              </Box>
                            ),
                            ol: ({ children }) => (
                              <Box
                                component="ol"
                                sx={{
                                  margin: 0,
                                  paddingLeft: "1.5em",
                                }}
                              >
                                {children}
                              </Box>
                            ),
                            li: ({ children }) => (
                              <Box
                                component="li"
                                sx={{
                                  margin: 0,
                                }}
                              >
                                {children}
                              </Box>
                            ),
                          }}
                        >
                          {n.post.replace(/\n\n/g, "\n")}
                        </ReactMarkdown>
                      </Box>
                    </Box>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Box>
      </Collapse>

      {/* Note Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {selectedNoteId &&
          (() => {
            const note = notes.find((n) => n.id === selectedNoteId);
            if (!note) return null;
            const { canEdit, canDelete } = notePermissions[selectedNoteId] || {
              canEdit: false,
              canDelete: false,
            };
            return (
              <>
                {canEdit && (
                  <MenuItem onClick={() => handleEdit(note)}>
                    <ListItemIcon>
                      <EditIcon fontSize="small" sx={{ color: "#4467A5" }} />
                    </ListItemIcon>
                    <ListItemText primary={t("notes.editButton")} />
                  </MenuItem>
                )}
                {canDelete && (
                  <MenuItem onClick={() => handleDelete(selectedNoteId)}>
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" sx={{ color: "#D32F2F" }} />
                    </ListItemIcon>
                    <ListItemText primary={t("notes.deleteButton")} />
                  </MenuItem>
                )}
              </>
            );
          })()}
      </Popover>

      {/* Attachment Popover */}
      <Popover
        open={Boolean(attachmentAnchorEl)}
        anchorEl={attachmentAnchorEl}
        onClose={handleCloseAttachmentMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {selectedNoteForAttachments &&
          selectedNoteForAttachments.attachmentsIds &&
          selectedNoteForAttachments.attachmentsIds.length > 0 && (
            <Box sx={{ p: 1 }}>
              {selectedNoteForAttachments.attachmentsIds.map((attachmentId) => {
                if (
                  !selectedNoteForAttachments.attachmentsNames ||
                  !selectedNoteForAttachments.attachmentsTypes
                )
                  return null;
                const attachmentName =
                  selectedNoteForAttachments.attachmentsNames[attachmentId] ||
                  "Unknown file";

                const { canEdit, canDelete } = attachmentPermissions[
                  attachmentId
                ] || {
                  canEdit: false,
                  canDelete: false,
                };
                return (
                  <Box
                    key={attachmentId}
                    sx={{
                      border: "1px solid",
                      borderColor: "grey.300",
                      borderRadius: 1,
                      p: 1,
                      mb: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <UploadFileIcon sx={{ color: "#99A2AD" }} />

                      <Typography
                        sx={{
                          fontSize: { xs: "0.8rem", sm: "0.875rem" },
                          fontWeight: "medium",
                        }}
                      >
                        {attachmentName}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          downloadAttachment(attachmentId, attachmentName)
                        }
                        disabled={loading}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                      {(canEdit || canDelete) && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setSelectedAttachmentId(attachmentId);
                            setAttachmentAnchorEl(e.currentTarget);
                          }}
                          disabled={loading}
                        >
                          <MoreVertOutlinedIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    {(canEdit || canDelete) &&
                      selectedAttachmentId === attachmentId && (
                        <Popover
                          open={
                            Boolean(attachmentAnchorEl) &&
                            selectedAttachmentId === attachmentId
                          }
                          anchorEl={attachmentAnchorEl}
                          onClose={handleCloseAttachmentMenu}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                        >
                          {canDelete && (
                            <MenuItem
                              onClick={() =>
                                handleAttachmentDelete(attachmentId)
                              }
                            >
                              <ListItemIcon>
                                <DeleteIcon
                                  fontSize="small"
                                  sx={{ color: "#D32F2F" }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={t("attachments.deleteButton")}
                              />
                            </MenuItem>
                          )}
                        </Popover>
                      )}
                  </Box>
                );
              })}
            </Box>
          )}
      </Popover>

      {/* NoteForm & ConfirmDialog */}
      <NoteForm
        open={formOpen}
        parentId={parentId}
        note={noteForEdit}
        onClose={() => {
          setFormOpen(false);
          setNoteForEdit(null);
        }}
        onCreated={async () => {
          setFormOpen(false);
          setNoteForEdit(null);
          await onRefresh();
          await loadNotes();
        }}
        onUpdated={async () => {
          setFormOpen(false);
          setNoteForEdit(null);
          await onRefresh();
          await loadNotes();
        }}
      />
      <AttachmentForm
        open={attachmentFormOpen}
        parentId={parentId}
        onClose={() => {
          setAttachmentFormOpen(false);
          setSelectedAttachmentId(null);
        }}
        onCreated={handleAttachmentCreated}
        attachment={
          selectedNoteForAttachments?.attachmentsIds && selectedAttachmentId
            ? {
                id: selectedAttachmentId,
                name:
                  selectedNoteForAttachments.attachmentsNames?.[
                    selectedAttachmentId
                  ] || "Unknown file",
                url: getAttachmentUrl(selectedAttachmentId),
                type:
                  selectedNoteForAttachments.attachmentsTypes?.[
                    selectedAttachmentId
                  ] || "",
              }
            : null
        }
      />
      <ConfirmDialog
        open={confirmOpen}
        title={t("notes.deleteConfirmTitle")}
        content={t("notes.deleteConfirmContent")}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={loading}
      />
      <ConfirmDialog
        open={attachmentConfirmOpen}
        title={t("attachments.deleteConfirmTitle")}
        content={t("attachments.deleteConfirmContent")}
        onConfirm={handleConfirmAttachmentDelete}
        onCancel={handleCancelAttachmentDelete}
        loading={loading}
      />
      <Notification
        open={!!error}
        message={error || ""}
        severity="error"
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
      <Notification
        open={!!success}
        message={success || ""}
        severity="success"
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </Box>
  );
};

export default NotesAccordion;
